import { R as derived, T as props_id, U as attributes, S as bind_props, V as spread_props, X as attr_class, W as clsx, Z as ensure_array_like, a2 as stringify } from "./index2.js";
import { c as createBitsAttrs, C as Context, a as attachRef, P as PresenceManager, b as boxWith, h as SPACE, E as ENTER, w as watch, u as afterTick, R as RovingFocusGroup, j as boolToEmptyStrOrUndef, g as getDataOpenClosed, y as boolToStr, m as mergeProps, d as createId, n as noop, I as Icon, e as cn } from "./Icon4.js";
import { o as on } from "./events.js";
import "clsx";
import { f as fallback, e as escape_html } from "./render-context.js";
import { A as Animate } from "./Animate.js";
import { L as Link } from "./Link.js";
const accordionAttrs = createBitsAttrs({
  component: "accordion",
  parts: ["root", "trigger", "content", "item", "header"]
});
const AccordionRootContext = new Context("Accordion.Root");
const AccordionItemContext = new Context("Accordion.Item");
class AccordionBaseState {
  opts;
  rovingFocusGroup;
  attachment;
  constructor(opts) {
    this.opts = opts;
    this.rovingFocusGroup = new RovingFocusGroup({
      rootNode: this.opts.ref,
      candidateAttr: accordionAttrs.trigger,
      loop: this.opts.loop,
      orientation: this.opts.orientation
    });
    this.attachment = attachRef(this.opts.ref);
  }
  #props = derived(() => ({
    id: this.opts.id.current,
    "data-orientation": this.opts.orientation.current,
    "data-disabled": boolToEmptyStrOrUndef(this.opts.disabled.current),
    [accordionAttrs.root]: "",
    ...this.attachment
  }));
  get props() {
    return this.#props();
  }
  set props($$value) {
    return this.#props($$value);
  }
}
class AccordionSingleState extends AccordionBaseState {
  opts;
  isMulti = false;
  constructor(opts) {
    super(opts);
    this.opts = opts;
    this.includesItem = this.includesItem.bind(this);
    this.toggleItem = this.toggleItem.bind(this);
  }
  includesItem(item) {
    return this.opts.value.current === item;
  }
  toggleItem(item) {
    this.opts.value.current = this.includesItem(item) ? "" : item;
  }
}
class AccordionMultiState extends AccordionBaseState {
  #value;
  isMulti = true;
  constructor(props) {
    super(props);
    this.#value = props.value;
    this.includesItem = this.includesItem.bind(this);
    this.toggleItem = this.toggleItem.bind(this);
  }
  includesItem(item) {
    return this.#value.current.includes(item);
  }
  toggleItem(item) {
    this.#value.current = this.includesItem(item) ? this.#value.current.filter((v) => v !== item) : [...this.#value.current, item];
  }
}
class AccordionRootState {
  static create(props) {
    const { type, ...rest } = props;
    const rootState = type === "single" ? new AccordionSingleState(rest) : new AccordionMultiState(rest);
    return AccordionRootContext.set(rootState);
  }
}
class AccordionItemState {
  static create(props) {
    return AccordionItemContext.set(new AccordionItemState({ ...props, rootState: AccordionRootContext.get() }));
  }
  opts;
  root;
  #isActive = derived(() => this.root.includesItem(this.opts.value.current));
  get isActive() {
    return this.#isActive();
  }
  set isActive($$value) {
    return this.#isActive($$value);
  }
  #isDisabled = derived(() => this.opts.disabled.current || this.root.opts.disabled.current);
  get isDisabled() {
    return this.#isDisabled();
  }
  set isDisabled($$value) {
    return this.#isDisabled($$value);
  }
  attachment;
  contentNode = null;
  contentPresence;
  constructor(opts) {
    this.opts = opts;
    this.root = opts.rootState;
    this.updateValue = this.updateValue.bind(this);
    this.attachment = attachRef(this.opts.ref);
    this.contentPresence = new PresenceManager({
      ref: boxWith(() => this.contentNode),
      open: boxWith(() => this.isActive)
    });
  }
  updateValue() {
    this.root.toggleItem(this.opts.value.current);
  }
  #props = derived(() => ({
    id: this.opts.id.current,
    "data-state": getDataOpenClosed(this.isActive),
    "data-disabled": boolToEmptyStrOrUndef(this.isDisabled),
    "data-orientation": this.root.opts.orientation.current,
    [accordionAttrs.item]: "",
    ...this.attachment
  }));
  get props() {
    return this.#props();
  }
  set props($$value) {
    return this.#props($$value);
  }
}
class AccordionTriggerState {
  opts;
  itemState;
  #root;
  #isDisabled = derived(() => this.opts.disabled.current || this.itemState.opts.disabled.current || this.#root.opts.disabled.current);
  attachment;
  constructor(opts, itemState) {
    this.opts = opts;
    this.itemState = itemState;
    this.#root = itemState.root;
    this.onclick = this.onclick.bind(this);
    this.onkeydown = this.onkeydown.bind(this);
    this.attachment = attachRef(this.opts.ref);
  }
  static create(props) {
    return new AccordionTriggerState(props, AccordionItemContext.get());
  }
  onclick(e) {
    if (this.#isDisabled() || e.button !== 0) {
      e.preventDefault();
      return;
    }
    this.itemState.updateValue();
  }
  onkeydown(e) {
    if (this.#isDisabled()) return;
    if (e.key === SPACE || e.key === ENTER) {
      e.preventDefault();
      this.itemState.updateValue();
      return;
    }
    this.#root.rovingFocusGroup.handleKeydown(this.opts.ref.current, e);
  }
  #props = derived(() => ({
    id: this.opts.id.current,
    disabled: this.#isDisabled(),
    "aria-expanded": boolToStr(this.itemState.isActive),
    "aria-disabled": boolToStr(this.#isDisabled()),
    "data-disabled": boolToEmptyStrOrUndef(this.#isDisabled()),
    "data-state": getDataOpenClosed(this.itemState.isActive),
    "data-orientation": this.#root.opts.orientation.current,
    [accordionAttrs.trigger]: "",
    tabindex: 0,
    onclick: this.onclick,
    onkeydown: this.onkeydown,
    ...this.attachment
  }));
  get props() {
    return this.#props();
  }
  set props($$value) {
    return this.#props($$value);
  }
}
class AccordionContentState {
  opts;
  item;
  attachment;
  #originalStyles = void 0;
  #isMountAnimationPrevented = false;
  #dimensions = { width: 0, height: 0 };
  #open = derived(() => {
    if (this.opts.hiddenUntilFound.current) return this.item.isActive;
    return this.opts.forceMount.current || this.item.isActive;
  });
  get open() {
    return this.#open();
  }
  set open($$value) {
    return this.#open($$value);
  }
  constructor(opts, item) {
    this.opts = opts;
    this.item = item;
    this.#isMountAnimationPrevented = this.item.isActive;
    this.attachment = attachRef(this.opts.ref, (v) => this.item.contentNode = v);
    watch.pre(
      [
        () => this.opts.ref.current,
        () => this.opts.hiddenUntilFound.current
      ],
      ([node, hiddenUntilFound]) => {
        if (!node || !hiddenUntilFound) return;
        const handleBeforeMatch = () => {
          if (this.item.isActive) return;
          requestAnimationFrame(() => {
            this.item.updateValue();
          });
        };
        return on(node, "beforematch", handleBeforeMatch);
      }
    );
    watch([() => this.open, () => this.opts.ref.current], this.#updateDimensions);
  }
  static create(props) {
    return new AccordionContentState(props, AccordionItemContext.get());
  }
  #updateDimensions = ([_, node]) => {
    if (!node) return;
    afterTick(() => {
      const element = this.opts.ref.current;
      if (!element) return;
      this.#originalStyles ??= {
        transitionDuration: element.style.transitionDuration,
        animationName: element.style.animationName
      };
      element.style.transitionDuration = "0s";
      element.style.animationName = "none";
      const rect = element.getBoundingClientRect();
      this.#dimensions = { width: rect.width, height: rect.height };
      if (!this.#isMountAnimationPrevented && this.#originalStyles) {
        element.style.transitionDuration = this.#originalStyles.transitionDuration;
        element.style.animationName = this.#originalStyles.animationName;
      }
    });
  };
  get shouldRender() {
    return this.item.contentPresence.shouldRender;
  }
  #snippetProps = derived(() => ({ open: this.item.isActive }));
  get snippetProps() {
    return this.#snippetProps();
  }
  set snippetProps($$value) {
    return this.#snippetProps($$value);
  }
  #props = derived(() => ({
    id: this.opts.id.current,
    "data-state": getDataOpenClosed(this.item.isActive),
    "data-disabled": boolToEmptyStrOrUndef(this.item.isDisabled),
    "data-orientation": this.item.root.opts.orientation.current,
    [accordionAttrs.content]: "",
    style: {
      "--bits-accordion-content-height": `${this.#dimensions.height}px`,
      "--bits-accordion-content-width": `${this.#dimensions.width}px`
    },
    hidden: this.opts.hiddenUntilFound.current && !this.item.isActive ? "until-found" : void 0,
    ...this.opts.hiddenUntilFound.current && !this.shouldRender ? {} : {
      hidden: this.opts.hiddenUntilFound.current ? !this.shouldRender : this.opts.forceMount.current ? void 0 : !this.shouldRender
    },
    ...this.attachment
  }));
  get props() {
    return this.#props();
  }
  set props($$value) {
    return this.#props($$value);
  }
}
class AccordionHeaderState {
  opts;
  item;
  attachment;
  constructor(opts, item) {
    this.opts = opts;
    this.item = item;
    this.attachment = attachRef(this.opts.ref);
  }
  static create(props) {
    return new AccordionHeaderState(props, AccordionItemContext.get());
  }
  #props = derived(() => ({
    id: this.opts.id.current,
    role: "heading",
    "aria-level": this.opts.level.current,
    "data-heading-level": this.opts.level.current,
    "data-state": getDataOpenClosed(this.item.isActive),
    "data-orientation": this.item.root.opts.orientation.current,
    [accordionAttrs.header]: "",
    ...this.attachment
  }));
  get props() {
    return this.#props();
  }
  set props($$value) {
    return this.#props($$value);
  }
}
function Accordion$1($$renderer, $$props) {
  $$renderer.component(($$renderer2) => {
    const uid = props_id($$renderer2);
    let {
      disabled = false,
      children,
      child,
      type,
      value = void 0,
      ref = null,
      id = createId(uid),
      onValueChange = noop,
      loop = true,
      orientation = "vertical",
      $$slots,
      $$events,
      ...restProps
    } = $$props;
    function handleDefaultValue() {
      if (value !== void 0) return;
      value = type === "single" ? "" : [];
    }
    handleDefaultValue();
    watch.pre(() => value, () => {
      handleDefaultValue();
    });
    const rootState = AccordionRootState.create({
      type,
      value: boxWith(() => value, (v) => {
        value = v;
        onValueChange(v);
      }),
      id: boxWith(() => id),
      disabled: boxWith(() => disabled),
      loop: boxWith(() => loop),
      orientation: boxWith(() => orientation),
      ref: boxWith(() => ref, (v) => ref = v)
    });
    const mergedProps = mergeProps(restProps, rootState.props);
    if (child) {
      $$renderer2.push("<!--[-->");
      child($$renderer2, { props: mergedProps });
      $$renderer2.push(`<!---->`);
    } else {
      $$renderer2.push("<!--[!-->");
      $$renderer2.push(`<div${attributes({ ...mergedProps })}>`);
      children?.($$renderer2);
      $$renderer2.push(`<!----></div>`);
    }
    $$renderer2.push(`<!--]-->`);
    bind_props($$props, { value, ref });
  });
}
function Accordion_item$1($$renderer, $$props) {
  $$renderer.component(($$renderer2) => {
    const uid = props_id($$renderer2);
    const defaultId = createId(uid);
    let {
      id = defaultId,
      disabled = false,
      value = defaultId,
      children,
      child,
      ref = null,
      $$slots,
      $$events,
      ...restProps
    } = $$props;
    const itemState = AccordionItemState.create({
      value: boxWith(() => value),
      disabled: boxWith(() => disabled),
      id: boxWith(() => id),
      ref: boxWith(() => ref, (v) => ref = v)
    });
    const mergedProps = mergeProps(restProps, itemState.props);
    if (child) {
      $$renderer2.push("<!--[-->");
      child($$renderer2, { props: mergedProps });
      $$renderer2.push(`<!---->`);
    } else {
      $$renderer2.push("<!--[!-->");
      $$renderer2.push(`<div${attributes({ ...mergedProps })}>`);
      children?.($$renderer2);
      $$renderer2.push(`<!----></div>`);
    }
    $$renderer2.push(`<!--]-->`);
    bind_props($$props, { ref });
  });
}
function Accordion_header($$renderer, $$props) {
  $$renderer.component(($$renderer2) => {
    const uid = props_id($$renderer2);
    let {
      id = createId(uid),
      level = 2,
      children,
      child,
      ref = null,
      $$slots,
      $$events,
      ...restProps
    } = $$props;
    const headerState = AccordionHeaderState.create({
      id: boxWith(() => id),
      level: boxWith(() => level),
      ref: boxWith(() => ref, (v) => ref = v)
    });
    const mergedProps = mergeProps(restProps, headerState.props);
    if (child) {
      $$renderer2.push("<!--[-->");
      child($$renderer2, { props: mergedProps });
      $$renderer2.push(`<!---->`);
    } else {
      $$renderer2.push("<!--[!-->");
      $$renderer2.push(`<div${attributes({ ...mergedProps })}>`);
      children?.($$renderer2);
      $$renderer2.push(`<!----></div>`);
    }
    $$renderer2.push(`<!--]-->`);
    bind_props($$props, { ref });
  });
}
function Accordion_trigger$1($$renderer, $$props) {
  $$renderer.component(($$renderer2) => {
    const uid = props_id($$renderer2);
    let {
      disabled = false,
      ref = null,
      id = createId(uid),
      children,
      child,
      $$slots,
      $$events,
      ...restProps
    } = $$props;
    const triggerState = AccordionTriggerState.create({
      disabled: boxWith(() => disabled),
      id: boxWith(() => id),
      ref: boxWith(() => ref, (v) => ref = v)
    });
    const mergedProps = mergeProps(restProps, triggerState.props);
    if (child) {
      $$renderer2.push("<!--[-->");
      child($$renderer2, { props: mergedProps });
      $$renderer2.push(`<!---->`);
    } else {
      $$renderer2.push("<!--[!-->");
      $$renderer2.push(`<button${attributes({ type: "button", ...mergedProps })}>`);
      children?.($$renderer2);
      $$renderer2.push(`<!----></button>`);
    }
    $$renderer2.push(`<!--]-->`);
    bind_props($$props, { ref });
  });
}
function Accordion_content$1($$renderer, $$props) {
  $$renderer.component(($$renderer2) => {
    const uid = props_id($$renderer2);
    let {
      child,
      ref = null,
      id = createId(uid),
      forceMount = false,
      children,
      hiddenUntilFound = false,
      $$slots,
      $$events,
      ...restProps
    } = $$props;
    const contentState = AccordionContentState.create({
      forceMount: boxWith(() => forceMount),
      id: boxWith(() => id),
      ref: boxWith(() => ref, (v) => ref = v),
      hiddenUntilFound: boxWith(() => hiddenUntilFound)
    });
    const mergedProps = mergeProps(restProps, contentState.props);
    if (child) {
      $$renderer2.push("<!--[-->");
      child($$renderer2, { props: mergedProps, ...contentState.snippetProps });
      $$renderer2.push(`<!---->`);
    } else {
      $$renderer2.push("<!--[!-->");
      $$renderer2.push(`<div${attributes({ ...mergedProps })}>`);
      children?.($$renderer2);
      $$renderer2.push(`<!----></div>`);
    }
    $$renderer2.push(`<!--]-->`);
    bind_props($$props, { ref });
  });
}
function Chevron_down($$renderer, $$props) {
  $$renderer.component(($$renderer2) => {
    /**
     * @license @lucide/svelte v0.561.0 - ISC
     *
     * ISC License
     *
     * Copyright (c) for portions of Lucide are held by Cole Bemis 2013-2023 as part of Feather (MIT). All other copyright (c) for Lucide are held by Lucide Contributors 2025.
     *
     * Permission to use, copy, modify, and/or distribute this software for any
     * purpose with or without fee is hereby granted, provided that the above
     * copyright notice and this permission notice appear in all copies.
     *
     * THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
     * WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
     * MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
     * ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
     * WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
     * ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
     * OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
     *
     * ---
     *
     * The MIT License (MIT) (for portions derived from Feather)
     *
     * Copyright (c) 2013-2023 Cole Bemis
     *
     * Permission is hereby granted, free of charge, to any person obtaining a copy
     * of this software and associated documentation files (the "Software"), to deal
     * in the Software without restriction, including without limitation the rights
     * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
     * copies of the Software, and to permit persons to whom the Software is
     * furnished to do so, subject to the following conditions:
     *
     * The above copyright notice and this permission notice shall be included in all
     * copies or substantial portions of the Software.
     *
     * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
     * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
     * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
     * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
     * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
     * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
     * SOFTWARE.
     *
     */
    let { $$slots, $$events, ...props } = $$props;
    const iconNode = [["path", { "d": "m6 9 6 6 6-6" }]];
    Icon($$renderer2, spread_props([
      { name: "chevron-down" },
      /**
       * @component @name ChevronDown
       * @description Lucide SVG icon component, renders SVG Element with children.
       *
       * @preview ![img](data:image/svg+xml;base64,PHN2ZyAgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIgogIHdpZHRoPSIyNCIKICBoZWlnaHQ9IjI0IgogIHZpZXdCb3g9IjAgMCAyNCAyNCIKICBmaWxsPSJub25lIgogIHN0cm9rZT0iIzAwMCIgc3R5bGU9ImJhY2tncm91bmQtY29sb3I6ICNmZmY7IGJvcmRlci1yYWRpdXM6IDJweCIKICBzdHJva2Utd2lkdGg9IjIiCiAgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIgogIHN0cm9rZS1saW5lam9pbj0icm91bmQiCj4KICA8cGF0aCBkPSJtNiA5IDYgNiA2LTYiIC8+Cjwvc3ZnPgo=) - https://lucide.dev/icons/chevron-down
       * @see https://lucide.dev/guide/packages/lucide-svelte - Documentation
       *
       * @param {Object} props - Lucide icons props and any valid SVG attribute
       * @returns {FunctionalComponent} Svelte component
       *
       */
      props,
      {
        iconNode,
        children: ($$renderer3) => {
          props.children?.($$renderer3);
          $$renderer3.push(`<!---->`);
        },
        $$slots: { default: true }
      }
    ]));
  });
}
function Accordion($$renderer, $$props) {
  $$renderer.component(($$renderer2) => {
    let { ref = null, value = void 0, $$slots, $$events, ...restProps } = $$props;
    let $$settled = true;
    let $$inner_renderer;
    function $$render_inner($$renderer3) {
      $$renderer3.push(`<!---->`);
      Accordion$1($$renderer3, spread_props([
        { "data-slot": "accordion" },
        restProps,
        {
          get ref() {
            return ref;
          },
          set ref($$value) {
            ref = $$value;
            $$settled = false;
          },
          get value() {
            return value;
          },
          set value($$value) {
            value = $$value;
            $$settled = false;
          }
        }
      ]));
      $$renderer3.push(`<!---->`);
    }
    do {
      $$settled = true;
      $$inner_renderer = $$renderer2.copy();
      $$render_inner($$inner_renderer);
    } while (!$$settled);
    $$renderer2.subsume($$inner_renderer);
    bind_props($$props, { ref, value });
  });
}
function Accordion_content($$renderer, $$props) {
  $$renderer.component(($$renderer2) => {
    let {
      ref = null,
      class: className,
      children,
      $$slots,
      $$events,
      ...restProps
    } = $$props;
    let $$settled = true;
    let $$inner_renderer;
    function $$render_inner($$renderer3) {
      $$renderer3.push(`<!---->`);
      Accordion_content$1($$renderer3, spread_props([
        {
          "data-slot": "accordion-content",
          class: "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm"
        },
        restProps,
        {
          get ref() {
            return ref;
          },
          set ref($$value) {
            ref = $$value;
            $$settled = false;
          },
          children: ($$renderer4) => {
            $$renderer4.push(`<div${attr_class(clsx(cn("pt-0 pb-4", className)))}>`);
            children?.($$renderer4);
            $$renderer4.push(`<!----></div>`);
          },
          $$slots: { default: true }
        }
      ]));
      $$renderer3.push(`<!---->`);
    }
    do {
      $$settled = true;
      $$inner_renderer = $$renderer2.copy();
      $$render_inner($$inner_renderer);
    } while (!$$settled);
    $$renderer2.subsume($$inner_renderer);
    bind_props($$props, { ref });
  });
}
function Accordion_item($$renderer, $$props) {
  $$renderer.component(($$renderer2) => {
    let {
      ref = null,
      class: className,
      $$slots,
      $$events,
      ...restProps
    } = $$props;
    let $$settled = true;
    let $$inner_renderer;
    function $$render_inner($$renderer3) {
      $$renderer3.push(`<!---->`);
      Accordion_item$1($$renderer3, spread_props([
        {
          "data-slot": "accordion-item",
          class: cn("border-b last:border-b-0", className)
        },
        restProps,
        {
          get ref() {
            return ref;
          },
          set ref($$value) {
            ref = $$value;
            $$settled = false;
          }
        }
      ]));
      $$renderer3.push(`<!---->`);
    }
    do {
      $$settled = true;
      $$inner_renderer = $$renderer2.copy();
      $$render_inner($$inner_renderer);
    } while (!$$settled);
    $$renderer2.subsume($$inner_renderer);
    bind_props($$props, { ref });
  });
}
function Accordion_trigger($$renderer, $$props) {
  $$renderer.component(($$renderer2) => {
    let {
      ref = null,
      class: className,
      level = 3,
      children,
      $$slots,
      $$events,
      ...restProps
    } = $$props;
    let $$settled = true;
    let $$inner_renderer;
    function $$render_inner($$renderer3) {
      $$renderer3.push(`<!---->`);
      Accordion_header($$renderer3, {
        level,
        class: "flex",
        children: ($$renderer4) => {
          $$renderer4.push(`<!---->`);
          Accordion_trigger$1($$renderer4, spread_props([
            {
              "data-slot": "accordion-trigger",
              class: cn("focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-start text-sm font-medium transition-all outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180", className)
            },
            restProps,
            {
              get ref() {
                return ref;
              },
              set ref($$value) {
                ref = $$value;
                $$settled = false;
              },
              children: ($$renderer5) => {
                children?.($$renderer5);
                $$renderer5.push(`<!----> `);
                Chevron_down($$renderer5, {
                  class: "text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200"
                });
                $$renderer5.push(`<!---->`);
              },
              $$slots: { default: true }
            }
          ]));
          $$renderer4.push(`<!---->`);
        },
        $$slots: { default: true }
      });
      $$renderer3.push(`<!---->`);
    }
    do {
      $$settled = true;
      $$inner_renderer = $$renderer2.copy();
      $$render_inner($$inner_renderer);
    } while (!$$settled);
    $$renderer2.subsume($$inner_renderer);
    bind_props($$props, { ref });
  });
}
function QA($$renderer, $$props) {
  let question = fallback($$props["question"], "");
  let answer = fallback($$props["answer"], "");
  let value = fallback($$props["value"], "");
  Accordion_item($$renderer, {
    value,
    children: ($$renderer2) => {
      Accordion_trigger($$renderer2, {
        children: ($$renderer3) => {
          $$renderer3.push(`<!---->${escape_html(question)}`);
        },
        $$slots: { default: true }
      });
      $$renderer2.push(`<!----> `);
      Accordion_content($$renderer2, {
        children: ($$renderer3) => {
          $$renderer3.push(`<!---->${escape_html(answer)}`);
        },
        $$slots: { default: true }
      });
      $$renderer2.push(`<!---->`);
    },
    $$slots: { default: true }
  });
  bind_props($$props, { question, answer, value });
}
function FAQ($$renderer) {
  const data = [
    {
      question: "How does it work?",
      answer: "Glimmr simplifies brightness adjustment by directly interfacing with your displays through DDC/CI. This provides the same outcome as using the built-in buttons on the display's back or sides, but with enhanced convenience and ease-of-use."
    },
    {
      question: "Can I adjust the brightness of individual displays in a multi-display setup?",
      answer: "Certainly! With Glimmr, you have the flexibility to adjust the brightness of individual displays in a multi-display setup. The tool recognizes and allows you to control each connected display separately. This means you can fine-tune the brightness settings of each display independently, ensuring optimal visual experience and comfort across your entire multi-display configuration. Glimmr provides you with the granular control needed to tailor the brightness levels of each display to your specific preferences and requirements."
    },
    {
      question: "Can I set a global brightness level?",
      answer: "Yes, you have the ability to set a global brightness level for all your displays simultaneously with a single slider. This means you can adjust the brightness of all connected displays at once, ensuring a uniform and consistent brightness experience across your entire display setup. It's a convenient feature that saves you time and effort, as you don't have to adjust each display individually."
    },
    {
      question: "Is the app compatible with all versions of Windows?",
      answer: "Glimmr should run on all versions of Windows 7 and up, including Windows 8, 10, and 11."
    },
    {
      question: "Does the app work with displays from different manufacturers?",
      answer: "Absolutely! Glimmr utilizes DDC/CI to directly adjust brightness, ensuring compatibility with a wide range of displays. In case you encounter flickering or consistent brightness levels, consider trying a different connection type (preferably direct instead of using adapters or USB-C hubs). Alternatively, you can utilize Shade Mode, which works seamlessly on all displays regardless of the connection type."
    },
    {
      question: "What is Shade Mode?",
      answer: "With a premium license, you can easily activate Shade Mode on any display. This feature enables you to adjust the brightness of your displays by creating an Always-On-Top (AOT) semi-transparent window on the desired display. Please note that certain native Windows elements, such as the taskbar, may appear above this window when focused. Shade Mode is particularly useful if you frequently rely on adapters or USB-C hubs for display connections, encounter issues when adjusting brightness natively, or prefer setting your displays to lower brightness levels than what they typically offer."
    },
    {
      question: "Can I schedule automatic brightness adjustments based on specific times of day?",
      answer: "Absolutely! The scheduling feature allows you to create a custom brightness schedule tailored to your preferences. Simply specify the target displays, desired times, and corresponding brightness levels, and Glimmr will automatically adjust the brightness of your displays according to the schedule you've set. This enables you to have an optimized viewing experience at different times, without the need for manual adjustments."
    },
    {
      question: "Is the app compatible with both desktop and laptop computers?",
      answer: "Yes, you can use Glimmr to adjust both internal and external displays. It can also be used from a laptop or desktop, whichever you prefer."
    },
    {
      question: "Can I try Glimmr before purchasing a license?",
      answer: "Absolutely – you can download and install Glimmr for free to try out its features. The free version includes basic functionality to get you started. If you find Glimmr useful and want to unlock additional features, such as scheduling, Shade Mode, and more, you can purchase a premium license to access the full suite of capabilities."
    },
    {
      question: "I'm having trouble activating my license. What should I do?",
      answer: "If you're experiencing issues activating your license, please ensure that you're connected to the internet and that your firewall or antivirus software isn't blocking Glimmr's connection to the licensing server. If you continue to encounter issues, please reach out to our support team at support@glimmr.app for further assistance."
    },
    {
      question: "I never received my license key. What should I do?",
      answer: "If you haven't received your license key after your purchase, please make sure to check your spam or junk folder. If you still can't find it, please reach out to our support team and we'll be happy to assist you."
    },
    {
      question: "Can I use Glimmr on multiple devices with a single license?",
      answer: "Yes! Your license key can be used on an unlimited number of devices. This flexibility ensures that you can access Glimmr's functionality wherever you need it, without any restrictions."
    }
  ];
  $$renderer.push(`<section class="w-full z-0"><div class="section flex flex-col items-center py-24 gap-12 text-center"><div class="mx-auto max-w-4xl text-center"><span class="text-base/7 font-semibold text-teal-600 font-display">FAQ</span> <h2 class="mt-2 font-display text-balance text-5xl font-semibold tracking-tight text-gray-900 sm:text-6xl">Got Questions? We've Got Answers.</h2> <div class="mx-auto mt-8 max-w-2xl text-pretty text-center text-lg text-gray-600 sm:text-xl/8">Check out our frequently asked questions below. If you have any other questions, or
				just wanna chat, feel free to reach out to us at <span class="inline-block text-teal-500">`);
  Link($$renderer, {
    to: "mailto:support@glimmr.app",
    children: ($$renderer2) => {
      $$renderer2.push(`<!---->support@glimmr.app`);
    },
    $$slots: { default: true }
  });
  $$renderer.push(`<!----></span></div></div> `);
  Accordion($$renderer, {
    children: ($$renderer2) => {
      $$renderer2.push(`<!--[-->`);
      const each_array = ensure_array_like(data);
      for (let i = 0, $$length = each_array.length; i < $$length; i++) {
        let content = each_array[i];
        Animate($$renderer2, {
          children: ($$renderer3) => {
            QA($$renderer3, {
              question: content.question,
              answer: content.answer,
              value: `item-${stringify(i)}`
            });
          },
          $$slots: { default: true }
        });
      }
      $$renderer2.push(`<!--]-->`);
    },
    $$slots: { default: true }
  });
  $$renderer.push(`<!----></div></section>`);
}
export {
  FAQ as F
};
